// Plain-English wording for the policy engine's vocabulary.
//
// The engine speaks in predicate keys (`role_in`, `gross_salary_lte`), row
// scopes (`branch` / `global`) and numeric priorities. None of that means
// anything to the person reading the Policy Rules screen, so every label the
// page shows is translated here rather than printed raw.
//
// Unknown keys must still read sensibly — the catalog is server-driven and
// grows — so anything missing from the table is derived from its suffix.

const PREDICATE_LABELS: Record<string, string> = {
  employee_in: 'Only certain employees',
  employee_not_in: 'Everyone except certain employees',
  role_in: 'Role is',
  role_not_in: 'Role is not',
  gender: 'Gender is',
  department_in: 'Department is',
  designation_in: 'Designation is',
  gross_salary_lte: 'Gross salary is at most',
  gross_salary_gte: 'Gross salary is at least',
  tenure_years_gte: 'Has worked here at least (years)',
  work_mode_in: 'Work mode is',
  work_location_in: 'Work location is',
};

// Enum values arrive as engine tokens ("reporting_manager", "allow_and_flag").
// Printed raw they read like database columns; these are the words on screen.
const CHOICE_WORDS: Record<string, string> = {
  hr: 'HR',
  hod: 'HOD',
  ot: 'OT',
  gps: 'GPS',
  wfh: 'WFH',
};

/** One enum choice, as a person would write it: "Reporting manager", "HR admin". */
export function choiceLabel(raw: string): string {
  const words = String(raw || '').split('_').filter(Boolean);
  if (words.length === 0) return '';
  return words
    .map((word, i) => {
      const known = CHOICE_WORDS[word.toLowerCase()];
      if (known) return known;
      return i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word;
    })
    .join(' ');
}

const titleCase = (raw: string) =>
  raw.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

/** How a condition is named in the picker and beside its value. */
export function predicateLabel(key: string): string {
  const known = PREDICATE_LABELS[key];
  if (known) return known;
  // Order matters: `_not_in` has to be tested before `_in`.
  if (key.endsWith('_not_in')) return `${titleCase(key.slice(0, -7))} is not`;
  if (key.endsWith('_in')) return `${titleCase(key.slice(0, -3))} is`;
  if (key.endsWith('_gte')) return `${titleCase(key.slice(0, -4))} is at least`;
  if (key.endsWith('_lte')) return `${titleCase(key.slice(0, -4))} is at most`;
  return titleCase(key);
}

/** A condition's value, short enough to sit on one line. */
export function formatPredicateValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    const shown = value.slice(0, 3).map(String).join(', ');
    return value.length > 3 ? `${shown} +${value.length - 3} more` : shown;
  }
  return String(value);
}

// Short enough to name outright; beyond this a count reads better.
const NAMES_SHOWN = 3;

/**
 * Who a single rule row covers, as a sentence fragment.
 *
 * `names` maps an employee id to a display name. Pass it and rows naming a few
 * people read "Priya Sharma, Rahul K"; without it they fall back to a count,
 * because the raw ids are meaningless on screen.
 */
export function describeAudience(
  applies: Record<string, unknown>,
  names?: Map<string, string> | null,
): string {
  const keys = Object.keys(applies);
  if (keys.length === 0) return 'Everyone';

  return keys
    .map((key) => {
      const value = applies[key];
      if (key === 'employee_in' || key === 'employee_not_in') {
        const ids = (Array.isArray(value) ? value : [value]).map(String);
        const resolved = names
          ? ids.map((id) => names.get(id)).filter(Boolean) as string[]
          : [];
        const label =
          resolved.length === ids.length && ids.length <= NAMES_SHOWN
            ? resolved.join(', ')
            : `${ids.length} ${ids.length === 1 ? 'employee' : 'employees'}`;
        if (key === 'employee_not_in') return `everyone except ${label}`;
        // "Priya Sharma" already reads as an audience; a bare count does not.
        return resolved.length === ids.length && ids.length <= NAMES_SHOWN
          ? label
          : `${label} only`;
      }
      return `${predicateLabel(key)} ${formatPredicateValue(value)}`;
    })
    .join(' and ');
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-07-01" → "1 Jul 2026". Parsed by hand: `new Date(iso)` reads the
 *  string as UTC and prints the previous day east of Greenwich. */
export function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

/** The dates a rule is limited to, or null when it always applies. */
export function formatWindow(from: string | null, to: string | null): string | null {
  if (from && to) return `${formatDate(from)} – ${formatDate(to)}`;
  if (from) return `from ${formatDate(from)}`;
  if (to) return `until ${formatDate(to)}`;
  return null;
}

/** Branch rules are this company's own; global rows are the shared fallback. */
export const SCOPE_LABEL: Record<string, string> = {
  branch: 'Your organisation',
  global: 'Shared default',
};
